import { motion, useAnimation, useInView, useScroll, Variants } from 'framer-motion';
import React, { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';
import Lottie from 'lottie-react';
import brandstory_1 from './lotties/brandstory_1.json';
import brandstory_3 from './lotties/brandstory_3.json';
import brandstory_4 from './lotties/brandstory_4.json';
import useBreakpoints from '../hooks/useBreakpoints';

const VIDEO_WIDTH = 1280;
const VIDEO_HEIGHT = 720;

export default function StatisticSection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = VIDEO_WIDTH;
    canvas.height = VIDEO_HEIGHT;

    if (!videoRef.current) {
      const video = document.createElement('video');
      videoRef.current = video;

      const webmsrc = document.createElement('source');
      webmsrc.type = 'video/webm';
      webmsrc.src = 'https://cleanhome-web.cleaninglab.co.kr/brandsite/about/webm/brandstory_2.webm';

      const mp4src = document.createElement('source');
      mp4src.type = 'video/mp4';
      mp4src.src = 'https://cleanhome-web.cleaninglab.co.kr/brandsite/about/brandstory_2.mp4';

      video.appendChild(webmsrc);
      video.appendChild(mp4src);

      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = true;
    }

    const video = videoRef.current;
    video.addEventListener('loadeddata', () => {
      video.play();
    });
    drawCanvas(video);
  }, [media]);

  const drawCanvas = (video: HTMLVideoElement) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(video, 0, 0, VIDEO_WIDTH, VIDEO_HEIGHT);
    requestAnimationFrame(() => drawCanvas(video));
  };

  const cardGridRef = useRef<HTMLDivElement>(null);
  const cardInView = useInView(cardGridRef);
  const controls = useAnimation();

  const variants: Variants = {
    initial: { y: 48, opacity: 0, transition: { delay: 0, duration: 0.2 } },
    animate: (delay) => ({ y: 0, opacity: 1, transition: { delay, duration: 0.5, ease: 'easeOut' } }),
  };

  useEffect(() => {
    if (cardInView) controls.start('animate');
    else controls.start('initial');
  }, [cardInView]);

  return (
    <Container ref={containerRef}>
      <Wrapper>
        <TitleWrapper>
          <FakeTitle>우리의 만남으로 만들어온 기록들</FakeTitle>
          <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
          <TitleTablet>{TitleTabletSvg}</TitleTablet>
          <TitleMobile>{TitleMobileSvg}</TitleMobile>
        </TitleWrapper>
        <Content>
          <CardGrid ref={cardGridRef} initial="initial" animate={controls}>
            <MatchingCard variants={variants} custom={0}>
              <CardBack>
                <Lottie animationData={brandstory_1} autoPlay rendererSettings={{}} />
              </CardBack>
              <CardFront>
                <CardText>
                  <CardTitle>
                    고객과 매니저의
                    <br />
                    매칭 횟수
                  </CardTitle>
                  <CardDescription>
                    <CardNumber>870</CardNumber>
                    <CardNumberUnit>만</CardNumberUnit>
                  </CardDescription>
                </CardText>
              </CardFront>
            </MatchingCard>
            <JobCard variants={variants} custom={0.1}>
              <CardBack>
                <ManagerCanvas ref={canvasRef} />
              </CardBack>
              <CardFront>
                <CardText>
                  <CardTitle>
                    청연에서 새로운
                    <br />
                    일자리를 얻은 매니저
                  </CardTitle>
                  <CardDescription>
                    <CardNumber>127,656</CardNumber>
                    <CardNumberUnit>명</CardNumberUnit>
                  </CardDescription>
                </CardText>
              </CardFront>
            </JobCard>
            <HouseCard variants={variants} custom={0.2}>
              <CardBack>
                <Lottie animationData={brandstory_3} autoPlay rendererSettings={{}} />
              </CardBack>
              <CardFront>
                <CardText>
                  <CardTitle>청연으로 깨끗해진 집</CardTitle>
                  <CardDescription>
                    <CardNumber>246</CardNumber>
                    <CardNumberUnit>만</CardNumberUnit>
                  </CardDescription>
                </CardText>
              </CardFront>
            </HouseCard>
            <TimeCard variants={variants} custom={0.3}>
              <CardBack>
                <Lottie animationData={brandstory_4} autoPlay rendererSettings={{}} />
              </CardBack>
              <CardFront>
                <CardText>
                  <CardTitle>
                    월 평균 청소대신
                    <br />
                    절약한 시간
                  </CardTitle>
                  <CardDescription>
                    <CardNumber>22.3</CardNumber>
                    <CardNumberUnit>시간</CardNumberUnit>
                  </CardDescription>
                </CardText>
              </CardFront>
            </TimeCard>
          </CardGrid>
        </Content>
      </Wrapper>
    </Container>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  padding: 0 24px;
  padding-top: 150px;
  position: relative;
  background: #e9f7f8;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 74px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 66px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.mobile}; */
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  margin-bottom: 90px;
  justify-content: center;

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  margin-bottom: 74px;
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 60px;
    justify-content: flex-start;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const TitleMobile = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: block;
  }
`;

const Content = styled.div``;

const CardGrid = styled(motion.div)`
  width: 100%;
  display: grid;
  grid-template:
    'matching matching' 1fr
    'job house' 1fr
    'job time' 1fr
    / 1fr 1fr;

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  gap: 20px;
  height: 938px;
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    gap: 24px;
    height: 952px;
    grid-template:
      'matching' 1fr
      'job' 1fr
      'house' 1fr
      'time' 1fr
      / 1fr;
  }
`;

const CardTitle = styled.h2`
  font-weight: 400;
  line-height: 135%;

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  font-size: 24px;
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 18px;
    font-weight: 500;
  }
`;

const CardDescription = styled.p`
  white-space: nowrap;
  margin-top: auto;
`;

const CardNumber = styled.span`
  font-weight: 700;
`;

const CardNumberUnit = styled.span`
  font-weight: 800;
`;

const Card = styled(motion.div)`
  display: block;
  border-radius: 16px;
  background: #f5f5f5;
  position: relative;
  overflow: hidden;
  transition: 0.2s;
  isolation: isolate;

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  /* padding: 30px 30px; */
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    border-radius: 8px;
  }
`;

const CardBack = styled.div`
  position: absolute;
  width: 100%;
`;

const ManagerCanvas = styled.canvas`
  position: absolute;
  left: 50%;
  bottom: -10%;
  transform: translateX(-50%) scale(0.7);
  transform-origin: bottom;
  user-select: none;
  touch-action: none;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    transform: translateX(43%) scale(0.3);
    left: auto;
    right: 0%;
    bottom: -25%;
  }
`;

const CardFront = styled.div`
  position: relative;
  padding: 30px 30px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 24px 30px;
  }
`;

const CardBackground = styled.img`
  position: absolute;
  object-fit: cover;
`;

const CardText = styled.div`
  width: 100%;
  height: 100%;
`;

const MatchingCard = styled(Card)`
  grid-area: matching;
  background: #004a80;
  color: #ffffff;
  margin-bottom: 10px;

  ${CardBack} {
    left: 0;
    top: 0;
  }

  ${CardText} {
    display: flex;
    justify-content: space-between;
  }

  ${CardTitle} {
    font-size: 34px;
    line-height: 135%;
  }

  ${CardDescription} {
    display: flex;
    justify-content: center;
    line-height: 115px;
  }

  ${CardNumber} {
    font-size: 96px;
  }

  ${CardNumberUnit} {
    font-size: 86px;
  }

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  ${CardTitle} {
    font-size: 24px;
  }

  ${CardDescription} {
    line-height: 77px;
  }

  ${CardNumber} {
    font-size: 70px;
  }

  ${CardNumberUnit} {
    font-size: 64px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 0;

    ${CardBack} {
      top: auto;
      right: auto;
      left: 0%;
      bottom: -10%;

      & > div {
        max-width: 580px;
        min-width: 460px;
        margin-left: auto;
      }
    }

    ${CardTitle} {
      font-size: 18px;
    }

    ${CardDescription} {
      line-height: 44px;
    }

    ${CardNumber} {
      font-size: 47px;
    }

    ${CardNumberUnit} {
      font-size: 37px;
    }
  }
`;

const JobCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  grid-area: job;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.85);

  ${CardBack} {
    inset: 0;
  }

  ${CardBackground} {
    left: 0;
    bottom: 0;
    width: 100%;
  }

  ${CardTitle} {
    font-size: 32px;
    margin-bottom: 20px;
  }

  ${CardDescription} {
    position: relative;
    top: 0;
    left: -5px;
  }

  ${CardNumber} {
    font-size: 94px;
  }

  ${CardNumberUnit} {
    font-size: 48px;
  }

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  ${CardTitle} {
    font-size: 24px;
    margin-bottom: 8px;
  }

  ${CardDescription} {
  }

  ${CardNumber} {
    font-size: 70px;
  }

  ${CardNumberUnit} {
    font-size: 40px;
  }
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* ${CardBackground} {
      right: 0;
      left: auto;
      bottom: 0;
      width: 50%;
      max-width: 220px;
    } */

    ${CardTitle} {
      font-size: 18px;
    }

    ${CardDescription} {
      left: 0;
    }

    ${CardNumber} {
      font-size: 39px;
    }

    ${CardNumberUnit} {
      font-size: 17px;
      margin-left: 2px;
    }
  }
`;

const HouseCard = styled(Card)`
  position: relative;
  grid-area: house;
  background: #00bdd6;

  /* ${CardBackground} {
    object-fit: contain;
    bottom: 0;
    left: 0;
    height: 50%;
  } */

  ${CardBack} {
    bottom: -4%;
    left: 0;
  }

  ${CardTitle} {
    font-size: 32px;
    margin-bottom: 7px;
    color: #ffffff;
  }

  ${CardDescription} {
    position: relative;
    top: 0;
    left: -5px;
    display: flex;
    align-items: center;
  }

  ${CardNumber} {
    font-size: 91px;
    color: #ffffff;
  }

  ${CardNumberUnit} {
    font-size: 78px;
    color: #ffffff;
  }

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  ${CardTitle} {
    font-size: 24px;
  }

  ${CardDescription} {
    line-height: 68px;
  }

  ${CardNumber} {
    font-size: 68px;
  }

  ${CardNumberUnit} {
    font-size: 57px;
  }
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* ${CardBackground} {
      bottom: 0;
      right: 0;
      object-position: bottom;

      height: 70%;
      object-fit: contain;
      object-position: center;
    } */

    ${CardBack} {
      bottom: 10%;
      left: 0;
      transform: translateY(20%);
      min-width: 360px;
      display: flex;
      align-items: flex-end;
    }

    ${CardText} {
      display: flex;
      justify-content: space-between;
    }

    ${CardTitle} {
      font-size: 20px;
      margin-bottom: 0;
      width: 100px;
      word-break: keep-all;
    }

    ${CardDescription} {
      left: 0;
      display: flex;
      align-items: flex-start;
      line-height: 44px;
    }

    ${CardNumber} {
      font-size: 47px;
    }

    ${CardNumberUnit} {
      font-size: 37px;
    }
  }
`;

const TimeCard = styled(Card)`
  grid-area: time;
  background: #c3e7ea;
  mix-blend-mode: normal;

  ${CardFront} {
    height: 100%;
  }

  ${CardText} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  /* ${CardBackground} {
    width: 20%;
    left: auto;
    bottom: 10%;
    right: 10%;
  } */

  ${CardBack} {
    right: 0;
    bottom: 0;
  }

  ${CardTitle} {
    font-size: 32px;
    margin-bottom: 7px;
  }

  ${CardDescription} {
    position: relative;
    top: 0;
    left: -5px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }

  ${CardNumber} {
    font-size: 93px;
    line-height: 111px;
  }

  ${CardNumberUnit} {
    font-size: 70px;
    line-height: 84px;
  }

  /* @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) { */
  ${CardTitle} {
    font-size: 24px;
  }

  ${CardDescription} {
  }

  ${CardNumber} {
    font-size: 66px;
    line-height: 79px;
  }

  ${CardNumberUnit} {
    font-size: 35px;
    line-height: 42px;
  }
  /* } */

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* ${CardBackground} {
      width: 20%;
      max-width: 80px;
      bottom: 10%;
      right: 10%;
    } */

    ${CardBack} {
      bottom: 0;
      right: 0;
      max-width: 280px;
    }

    ${CardTitle} {
      font-size: 20px;
    }

    ${CardDescription} {
      left: 0;
      display: block;
      line-height: 35px;
    }

    ${CardNumber} {
      font-size: 48px;
      line-height: 58px;
    }

    ${CardNumberUnit} {
      font-size: 29px;
      line-height: 35px;
      margin-left: 4px;
    }
  }
`;

const TitleDesktopSvg = (
  <svg width="614" height="45" viewBox="0 0 614 45" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27.2343 11.516C27.2343 14.92 24.1523 17.634 19.9663 17.634C15.6423 17.634 12.6983 14.92 12.6983 11.516C12.6983 8.02 15.6423 5.536 19.9663 5.536C24.1523 5.536 27.2343 8.02 27.2343 11.516ZM22.4963 42.474V28.674H39.3323V24.35H0.600274V28.674H17.4823V42.474H22.4963ZM32.4783 11.516C32.4783 5.076 26.9583 0.981998 19.9663 0.981998C13.1583 0.981998 7.45427 5.03 7.45427 11.516C7.45427 18.324 13.4803 22.004 19.9663 22.004C26.3603 22.004 32.4783 18.324 32.4783 11.516ZM68.8607 23.43C63.5707 24.534 54.6467 24.994 49.7707 24.764V17.726H64.0767V2.868H44.5727V7.284H58.9707V13.402H44.6647V29.042C52.3007 29.41 63.2487 29.134 69.4127 27.708L68.8607 23.43ZM72.7247 39.07H77.7847V0.751998H72.7247V39.07ZM90.6611 12.114C90.6611 8.986 93.1911 6.41 96.6411 6.41C100.091 6.41 102.667 8.986 102.667 12.114C102.667 15.242 100.091 17.818 96.6411 17.818C93.1911 17.818 90.6611 15.242 90.6611 12.114ZM111.269 25.408C108.003 25.776 101.379 26.374 96.5491 26.512C94.5251 26.558 91.9951 26.604 89.6031 26.604H83.3471L83.8991 31.158C87.8551 31.158 93.5591 31.02 97.5151 30.79C101.885 30.652 108.141 30.146 111.637 29.548L111.269 25.408ZM113.845 39.116H118.997V0.751998H113.845V39.116ZM85.6011 12.114C85.6011 17.818 90.2471 22.096 96.6411 22.096C103.173 22.096 107.819 17.818 107.819 12.114C107.819 6.41 103.081 2.132 96.6411 2.132C90.2471 2.132 85.6011 6.41 85.6011 12.114ZM171.308 42.934V38.518H150.884V27.8H145.732V42.934H171.308ZM153.092 18.922H142.788V7.33H153.092V18.922ZM158.106 23.292V2.96H137.636V23.292H158.106ZM177.058 15.886V11.516H170.066V0.705998H164.96V30.928H170.066V15.886H177.058ZM206.356 39.622H192.05V31.02H206.356V39.622ZM211.462 43.992V26.65H186.944V43.992H211.462ZM202.676 15.518C197.386 16.53 190.762 17.174 184.828 16.99V2.27H179.676V21.636C187.588 21.866 197.064 21.36 203.09 20.026L202.676 15.518ZM218.454 14.92V10.504H211.462V0.659999H206.356V24.488H211.462V14.92H218.454ZM225.627 13.31C225.627 19.796 231.101 24.718 238.921 24.718C246.741 24.718 252.215 19.796 252.215 13.31C252.215 6.732 246.741 1.902 238.921 1.902C231.101 1.902 225.627 6.732 225.627 13.31ZM230.917 13.31C230.917 9.17 234.367 6.272 238.921 6.272C243.429 6.272 246.971 9.17 246.971 13.31C246.971 17.68 243.429 20.302 238.921 20.302C234.367 20.302 230.917 17.68 230.917 13.31ZM258.287 34.47V30.146H219.555V34.47H258.287ZM277.833 24.81V30.56H260.951V34.976H299.683V30.56H282.847V24.81H294.347V20.394H272.313V15.518H293.519V2.086H267.207V6.456H288.459V11.286H267.207V24.81H277.833ZM348.866 42.934V38.518H328.442V27.8H323.29V42.934H348.866ZM330.65 18.922H320.346V7.33H330.65V18.922ZM335.664 23.292V2.96H315.194V23.292H335.664ZM354.616 15.886V11.516H347.624V0.705998H342.518V30.928H347.624V15.886H354.616ZM389.159 44.636V40.404H366.849V37.414H388.009V26.236H361.881V30.376H382.995V33.366H361.881V44.636H389.159ZM394.449 23.43V19.106H355.717V23.43H394.449ZM388.239 15.794V11.516H367.217V5.628H388.101V1.258H362.111V15.794H388.239ZM403.323 15.012C403.323 10.55 405.991 7.836 409.671 7.836C413.351 7.836 416.111 10.55 416.111 15.012C416.111 19.474 413.397 22.188 409.671 22.188C405.945 22.188 403.323 19.474 403.323 15.012ZM398.125 15.058C398.125 22.096 403.001 26.834 409.671 26.834C414.685 26.834 418.687 24.12 420.435 19.888C420.757 19.014 421.033 18.094 421.171 17.128H428.301V39.07H433.407V0.751998H428.301V12.85H421.125C420.987 11.976 420.757 11.102 420.435 10.274C418.733 5.904 414.639 3.236 409.671 3.236C403.001 3.236 398.125 8.02 398.125 15.058ZM464.821 10.412C464.821 13.586 461.831 15.748 457.875 15.748C453.597 15.748 450.929 13.586 450.929 10.412C450.929 7.422 453.643 5.076 457.875 5.076C461.831 5.076 464.821 7.468 464.821 10.412ZM445.041 30.974V43.9H471.491V39.53H450.101V30.974H445.041ZM469.927 10.412C469.927 4.57 464.591 0.751998 457.875 0.751998C451.481 0.751998 445.823 4.57 445.823 10.412C445.823 15.426 449.089 18.186 453.413 19.428C454.057 19.612 454.701 19.75 455.391 19.842V22.97H438.509V27.34H477.241V22.97H460.405V19.842C461.003 19.75 461.647 19.612 462.291 19.428C466.569 18.186 469.927 15.288 469.927 10.412ZM495.282 3.42V7.882H509.91C509.312 15.15 503.286 22.326 493.212 26.006L496.156 30.192C502.182 27.754 506.92 24.304 510.324 19.842C513.728 15.38 515.43 9.906 515.476 3.42H495.282ZM522.468 39.07H527.62V0.751998H522.468V39.07ZM565.705 44.452V30.974H538.887V35.252H560.553V44.452H565.705ZM550.157 19.796V23.522H533.275V27.938H572.007V23.522H555.171V19.796H566.349V15.61H544.775V12.528H565.475V1.166H539.669V5.444H560.415V8.342H539.669V19.796H550.157ZM608.113 44.636V40.404H585.803V37.414H606.963V26.236H580.835V30.376H601.949V33.366H580.835V44.636H608.113ZM613.403 23.43V19.106H574.671V23.43H613.403ZM607.193 15.794V11.516H586.171V5.628H607.055V1.258H581.065V15.794H607.193Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="369" height="27" viewBox="0 0 369 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.686 7.042C15.686 9.04 13.877 10.633 11.42 10.633C8.882 10.633 7.154 9.04 7.154 7.042C7.154 4.99 8.882 3.532 11.42 3.532C13.877 3.532 15.686 4.99 15.686 7.042ZM12.905 25.213V17.113H22.787V14.575H0.053V17.113H9.962V25.213H12.905ZM18.764 7.042C18.764 3.262 15.524 0.858999 11.42 0.858999C7.424 0.858999 4.076 3.235 4.076 7.042C4.076 11.038 7.613 13.198 11.42 13.198C15.173 13.198 18.764 11.038 18.764 7.042ZM40.6589 14.035C37.5539 14.683 32.3159 14.953 29.4539 14.818V10.687H37.8509V1.966H26.4029V4.558H34.8539V8.149H26.4569V17.329C30.9389 17.545 37.3649 17.383 40.9829 16.546L40.6589 14.035ZM42.9269 23.215H45.8969V0.723999H42.9269V23.215ZM53.9948 7.393C53.9948 5.557 55.4798 4.045 57.5048 4.045C59.5298 4.045 61.0418 5.557 61.0418 7.393C61.0418 9.229 59.5298 10.741 57.5048 10.741C55.4798 10.741 53.9948 9.229 53.9948 7.393ZM66.0908 15.196C64.1738 15.412 60.2858 15.763 57.4508 15.844C56.2628 15.871 54.7778 15.898 53.3738 15.898H49.7018L50.0258 18.571C52.3478 18.571 55.6958 18.49 58.0178 18.355C60.5828 18.274 64.2548 17.977 66.3068 17.626L66.0908 15.196ZM67.6028 23.242H70.6268V0.723999H67.6028V23.242ZM51.0248 7.393C51.0248 10.741 53.7518 13.252 57.5048 13.252C61.3388 13.252 64.0658 10.741 64.0658 7.393C64.0658 4.045 61.2848 1.534 57.5048 1.534C53.7518 1.534 51.0248 4.045 51.0248 7.393ZM102.411 25.483V22.891H90.4231V16.6H87.3991V25.483H102.411ZM91.7191 11.389H85.6711V4.585H91.7191V11.389ZM94.6621 13.954V2.02H82.6471V13.954H94.6621ZM105.786 9.607V7.042H101.682V0.696999H98.6851V18.436H101.682V9.607H105.786ZM123.523 23.539H115.126V18.49H123.523V23.539ZM126.52 26.104V15.925H112.129V26.104H126.52ZM121.363 9.391C118.258 9.985 114.37 10.363 110.887 10.255V1.615H107.863V12.982C112.507 13.117 118.069 12.82 121.606 12.037L121.363 9.391ZM130.624 9.04V6.448H126.52V0.669999H123.523V14.656H126.52V9.04H130.624ZM135.374 8.095C135.374 11.902 138.587 14.791 143.177 14.791C147.767 14.791 150.98 11.902 150.98 8.095C150.98 4.234 147.767 1.399 143.177 1.399C138.587 1.399 135.374 4.234 135.374 8.095ZM138.479 8.095C138.479 5.665 140.504 3.964 143.177 3.964C145.823 3.964 147.902 5.665 147.902 8.095C147.902 10.66 145.823 12.199 143.177 12.199C140.504 12.199 138.479 10.66 138.479 8.095ZM154.544 20.515V17.977H131.81V20.515H154.544ZM166.557 14.845V18.22H156.648V20.812H179.382V18.22H169.5V14.845H176.25V12.253H163.317V9.391H175.764V1.507H160.32V4.072H172.794V6.907H160.32V14.845H166.557ZM209.33 25.483V22.891H197.342V16.6H194.318V25.483H209.33ZM198.638 11.389H192.59V4.585H198.638V11.389ZM201.581 13.954V2.02H189.566V13.954H201.581ZM212.705 9.607V7.042H208.601V0.696999H205.604V18.436H208.601V9.607H212.705ZM233.52 26.482V23.998H220.425V22.243H232.845V15.682H217.509V18.112H229.902V19.867H217.509V26.482H233.52ZM236.625 14.035V11.497H213.891V14.035H236.625ZM232.98 9.553V7.042H220.641V3.586H232.899V1.021H217.644V9.553H232.98ZM242.374 9.094C242.374 6.475 243.94 4.882 246.1 4.882C248.26 4.882 249.88 6.475 249.88 9.094C249.88 11.713 248.287 13.306 246.1 13.306C243.913 13.306 242.374 11.713 242.374 9.094ZM239.323 9.121C239.323 13.252 242.185 16.033 246.1 16.033C249.043 16.033 251.392 14.44 252.418 11.956C252.607 11.443 252.769 10.903 252.85 10.336H257.035V23.215H260.032V0.723999H257.035V7.825H252.823C252.742 7.312 252.607 6.799 252.418 6.313C251.419 3.748 249.016 2.182 246.1 2.182C242.185 2.182 239.323 4.99 239.323 9.121ZM279.011 6.394C279.011 8.257 277.256 9.526 274.934 9.526C272.423 9.526 270.857 8.257 270.857 6.394C270.857 4.639 272.45 3.262 274.934 3.262C277.256 3.262 279.011 4.666 279.011 6.394ZM267.401 18.463V26.05H282.926V23.485H270.371V18.463H267.401ZM282.008 6.394C282.008 2.965 278.876 0.723999 274.934 0.723999C271.181 0.723999 267.86 2.965 267.86 6.394C267.86 9.337 269.777 10.957 272.315 11.686C272.693 11.794 273.071 11.875 273.476 11.929V13.765H263.567V16.33H286.301V13.765H276.419V11.929C276.77 11.875 277.148 11.794 277.526 11.686C280.037 10.957 282.008 9.256 282.008 6.394ZM297.97 2.29V4.909H306.556C306.205 9.175 302.668 13.387 296.755 15.547L298.483 18.004C302.02 16.573 304.801 14.548 306.799 11.929C308.797 9.31 309.796 6.097 309.823 2.29H297.97ZM313.927 23.215H316.951V0.723999H313.927V23.215ZM339.845 26.374V18.463H324.104V20.974H336.821V26.374H339.845ZM330.719 11.902V14.089H320.81V16.681H343.544V14.089H333.662V11.902H340.223V9.445H327.56V7.636H339.71V0.966999H324.563V3.478H336.74V5.179H324.563V11.902H330.719ZM365.277 26.482V23.998H352.182V22.243H364.602V15.682H349.266V18.112H361.659V19.867H349.266V26.482H365.277ZM368.382 14.035V11.497H345.648V14.035H368.382ZM364.737 9.553V7.042H352.398V3.586H364.656V1.021H349.401V9.553H364.737Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleMobileSvg = (
  <svg width="176" height="65" viewBox="0 0 176 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.362 7.123C15.362 9.04 13.742 10.39 11.42 10.39C9.044 10.39 7.478 9.04 7.478 7.123C7.478 5.26 9.071 3.856 11.42 3.856C13.742 3.856 15.362 5.26 15.362 7.123ZM13.094 25.078V17.167H22.679V14.386H0.161V17.167H9.773V25.078H13.094ZM18.737 7.123C18.737 3.289 15.497 0.939999 11.42 0.939999C7.478 0.939999 4.103 3.235 4.103 7.123C4.103 11.308 7.667 13.279 11.42 13.279C15.146 13.279 18.737 11.281 18.737 7.123ZM39.9699 13.9C36.9999 14.494 32.0049 14.764 29.3319 14.629V10.822H37.4049V2.047H25.9839V4.855H34.0839V8.095H26.0379V17.356C30.4119 17.572 36.8649 17.356 40.3209 16.627L39.9699 13.9ZM42.0759 23.107H45.3699V0.831999H42.0759V23.107ZM64.7668 15.088C63.1198 15.304 59.3398 15.628 56.5318 15.709C54.1828 15.79 50.7268 15.79 48.8638 15.736L49.1878 18.679C51.5638 18.706 54.9118 18.571 57.1528 18.463C59.6908 18.328 63.2278 17.977 65.0368 17.734L64.7668 15.088ZM50.0518 7.474C50.0518 10.795 52.7518 13.306 56.5318 13.306C60.3388 13.306 63.0928 10.795 63.0928 7.474C63.0928 4.126 60.3388 1.588 56.5318 1.588C52.7518 1.588 50.0518 4.126 50.0518 7.474ZM53.2918 7.447C53.2918 5.665 54.7768 4.342 56.5318 4.342C58.3408 4.342 59.8528 5.665 59.8528 7.447C59.8528 9.202 58.3408 10.579 56.5318 10.579C54.7768 10.579 53.2918 9.202 53.2918 7.447ZM66.2518 23.134H69.5728V0.831999H66.2518V23.134ZM100.384 25.375V22.54H88.7741V16.519H85.4261V25.375H100.384ZM89.4491 11.254H84.0221V4.882H89.4491V11.254ZM92.7701 14.035V2.101H80.7281V14.035H92.7701ZM103.678 9.769V6.988H99.6551V0.831999H96.3611V18.382H99.6551V9.769H103.678ZM120.699 23.242H112.869V18.625H120.699V23.242ZM123.993 25.996V15.817H109.629V25.996H123.993ZM118.647 9.283C115.65 9.823 111.708 10.174 108.684 10.066V1.696H105.39V13.063C109.71 13.252 115.353 12.955 118.917 12.172L118.647 9.283ZM128.016 9.229V6.421H123.993V0.804999H120.699V14.656H123.993V9.229H128.016ZM132.347 8.149C132.347 12.01 135.533 14.872 140.177 14.872C144.821 14.872 148.034 12.01 148.034 8.149C148.034 4.288 144.821 1.453 140.177 1.453C135.533 1.453 132.347 4.288 132.347 8.149ZM135.803 8.149C135.803 5.827 137.666 4.288 140.177 4.288C142.688 4.288 144.578 5.827 144.578 8.149C144.578 10.552 142.688 12.037 140.177 12.037C137.666 12.037 135.803 10.552 135.803 8.149ZM151.436 20.542V17.788H128.918V20.542H151.436ZM172.723 14.953V12.145H160.195V9.58H172.237V1.561H156.874V4.315H168.889V6.907H156.874V14.953H162.868V18.058H153.256V20.812H175.774V18.058H166.189V14.953H172.723ZM20.303 63.375V60.54H8.693V54.519H5.345V63.375H20.303ZM9.368 49.254H3.941V42.882H9.368V49.254ZM12.689 52.035V40.101H0.647V52.035H12.689ZM23.597 47.769V44.988H19.574V38.832H16.28V56.382H19.574V47.769H23.597ZM43.9659 64.347V61.701H31.3299V60.216H43.3179V53.574H28.0359V56.193H40.0509V57.705H28.0359V64.347H43.9659ZM47.0169 52.17V49.416H24.4989V52.17H47.0169ZM43.4259 47.715V44.961H31.5189V41.883H43.3449V39.129H28.1979V47.715H43.4259ZM52.6978 47.121C52.6978 44.691 54.1828 43.152 56.1538 43.152C58.1788 43.152 59.6638 44.691 59.6638 47.121C59.6638 49.578 58.1788 51.09 56.1538 51.09C54.1828 51.09 52.6978 49.578 52.6978 47.121ZM49.4038 47.148C49.4038 51.279 52.2658 54.033 56.1538 54.033C59.6368 54.033 62.2828 51.9 62.8498 48.552H66.7108V61.107H69.9778V38.832H66.7108V45.771H62.8768C62.3098 42.396 59.6368 40.236 56.1538 40.236C52.2658 40.236 49.4038 42.99 49.4038 47.148ZM88.2677 44.475C88.2677 46.176 86.6477 47.391 84.4337 47.391C82.1387 47.391 80.5997 46.176 80.5997 44.475C80.5997 42.801 82.1657 41.586 84.4337 41.586C86.6477 41.586 88.2677 42.801 88.2677 44.475ZM76.9007 56.355V63.942H92.3717V61.188H80.2217V56.355H76.9007ZM91.4807 44.475C91.4807 41.046 88.3487 38.832 84.4337 38.832C80.6267 38.832 77.3867 41.019 77.3867 44.475C77.3867 47.688 79.8707 49.47 82.7867 49.956V51.63H73.1747V54.411H95.6927V51.63H86.1077V49.956C88.9967 49.47 91.4807 47.634 91.4807 44.475ZM106.47 40.371V43.179H114.759C114.462 47.094 111.141 51.198 105.309 53.331L107.145 56.031C114.516 52.98 118.404 47.634 118.377 40.371H106.47ZM122.103 61.107H125.397V38.832H122.103V61.107ZM147.818 64.266V56.328H132.158V59.055H144.47V64.266H147.818ZM148.196 50.037V47.364H135.938V45.825H147.71V39.075H132.617V41.775H144.389V43.179H132.617V50.037H138.53V51.981H128.918V54.789H151.436V51.981H141.851V50.037H148.196ZM172.723 64.347V61.701H160.087V60.216H172.075V53.574H156.793V56.193H168.808V57.705H156.793V64.347H172.723ZM175.774 52.17V49.416H153.256V52.17H175.774ZM172.183 47.715V44.961H160.276V41.883H172.102V39.129H156.955V47.715H172.183Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
